import { useState, useEffect } from "react";
import { Trash2, Plus, Loader2, BookOpen } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCourses = () => {
  const [courses, setCourses] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({
    name: "", description: "", language_id: "", level: "beginner",
    duration_weeks: 8, hours_per_class: 1, tutor_id: ""
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const headers = { Authorization: `Bearer ${token}` };
      const [coursesRes, langsRes, tutorsRes] = await Promise.all([
        axios.get(`${API}/courses`),
        axios.get(`${API}/languages`),
        axios.get(`${API}/tutors`, { headers })
      ]);
      setCourses(coursesRes.data);
      setLanguages(langsRes.data);
      setTutors(tutorsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const createCourse = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const token = localStorage.getItem("access_token");
      await axios.post(`${API}/courses`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Course created!");
      setForm({ name: "", description: "", language_id: "", level: "beginner", duration_weeks: 8, hours_per_class: 1, tutor_id: "" });
      setShowForm(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create course");
    } finally {
      setCreating(false);
    }
  };

  const deleteCourse = async () => {
    if (!deleteTarget) return;
    try {
      const token = localStorage.getItem("access_token");
      await axios.delete(`${API}/courses/${deleteTarget}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Course deleted");
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      toast.error("Failed to delete course");
    }
  };

  const getLangFlag = (langId) => {
    const lang = languages.find(l => l.id === langId);
    return lang ? lang.flag_emoji : "";
  };

  const getLangName = (langId) => {
    const lang = languages.find(l => l.id === langId);
    return lang ? lang.name : "Unknown";
  };

  if (loading) return <div className="flex justify-center py-12"><div className="spinner"></div></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2" data-testid="courses-title">Courses</h1>
          <p className="text-[#4A4A4A]">8-week language courses</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="brutalist-btn-primary flex items-center gap-2" data-testid="add-course-btn">
          <BookOpen className="w-5 h-5" /> New Course
        </button>
      </div>

      {showForm && (
        <form onSubmit={createCourse} className="brutalist-card p-6 mb-6 space-y-4" data-testid="add-course-form">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Course name (e.g., Basic French)" className="brutalist-input" required data-testid="course-name-input" />
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Course description" className="brutalist-input" rows={3} data-testid="course-desc-input" />
          <div className="grid grid-cols-2 gap-4">
            <select value={form.language_id} onChange={(e) => setForm({ ...form, language_id: e.target.value })} className="brutalist-input" required data-testid="course-language-select">
              <option value="">Select Language</option>
              {languages.map(l => <option key={l.id} value={l.id}>{l.flag_emoji} {l.name}</option>)}
            </select>
            <select value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} className="brutalist-input" data-testid="course-level-select">
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-bold">Weeks</label>
              <Input type="number" value={form.duration_weeks} onChange={(e) => setForm({ ...form, duration_weeks: parseInt(e.target.value) || 8 })} className="brutalist-input" min={1} max={52} data-testid="course-weeks-input" />
            </div>
            <div>
              <label className="text-sm font-bold">Hours/Class</label>
              <Input type="number" value={form.hours_per_class} onChange={(e) => setForm({ ...form, hours_per_class: parseFloat(e.target.value) || 1 })} className="brutalist-input" min={0.5} step={0.5} data-testid="course-hours-input" />
            </div>
          </div>
          <select value={form.tutor_id} onChange={(e) => setForm({ ...form, tutor_id: e.target.value })} className="brutalist-input" data-testid="course-tutor-select">
            <option value="">Assign Tutor (optional)</option>
            {tutors.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <button type="submit" disabled={creating} className="brutalist-btn-primary flex items-center gap-2" data-testid="create-course-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Create Course
          </button>
        </form>
      )}

      {courses.length === 0 ? (
        <div className="brutalist-card p-12 text-center"><p className="text-[#4A4A4A]">No courses yet. Create one above.</p></div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {courses.map(c => (
            <div key={c.id} className="brutalist-card p-6 relative" style={{ backgroundColor: c.level === "beginner" ? "#D6E6FA20" : c.level === "intermediate" ? "#FBE7C620" : "#FFAEBC20" }} data-testid={`course-card-${c.id}`}>
              <button onClick={() => setDeleteTarget(c.id)} className="absolute top-4 right-4 p-1 text-[#FF6B6B] hover:bg-[#FFAEBC] rounded" data-testid={`delete-course-${c.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
              <div className="text-3xl mb-2">{getLangFlag(c.language_id)}</div>
              <h3 className="font-bold text-lg pr-8">{c.name}</h3>
              <p className="text-sm text-[#4A4A4A] mt-1">{getLangName(c.language_id)} &middot; {c.level}</p>
              <p className="text-sm text-[#4A4A4A]">{c.enrollment_count || 0} enrolled &middot; {c.duration_weeks} weeks</p>
              {c.tutor_name && <p className="text-sm mt-2 font-medium">Tutor: {c.tutor_name}</p>}
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Course?"
        description="This will permanently delete this course and all related enrollments and sessions. This action cannot be undone."
        onConfirm={deleteCourse}
        destructive
      />
    </div>
  );
};

export default AdminCourses;
